import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { Tax, TaxesService } from '../taxes.service';
import { TaxFormDialogComponent } from '../tax-form-dialog/tax-form-dialog.component';
import { HasPermissionDirective } from '../../../../../core/directives/has-permission.directive';
import { ConfirmationService } from '../../../../../shared/services/confirmation.service';
import { PageHeaderService } from '../../../../../shared/services/page-header.service';
import { SharedModule } from '../../../../../shared/shared.module';

@Component({
  selector: 'app-taxes-list',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDialogModule,
    MatMenuModule,
    MatDividerModule,
    HasPermissionDirective,
    SharedModule,
  ],
  template: `
    <section class="config-page">
      <app-card variant="default" padding="none" class="config-table-card">
        <div *ngIf="loading" class="loading-container">
          <mat-progress-spinner mode="indeterminate" diameter="50"></mat-progress-spinner>
          <p>Loading taxes...</p>
        </div>

        <div *ngIf="!loading && items.length === 0" class="empty-state">
          <div class="empty-state__icon"><mat-icon>percent</mat-icon></div>
          <h3>No taxes yet</h3>
          <p>Add your first tax (e.g. VAT or service charge) to get started.</p>
          <button mat-flat-button color="primary" (click)="onCreate()" *appHasPermission="'tax.create'">
            <mat-icon>add</mat-icon> Add Tax
          </button>
        </div>

        <div *ngIf="!loading && items.length > 0" class="table-container">
          <table mat-table [dataSource]="items" class="modern-table">
            <ng-container matColumnDef="name">
              <th mat-header-cell *matHeaderCellDef>Tax</th>
              <td mat-cell *matCellDef="let t">
                <div class="entity-cell">
                  <div class="entity-cell__icon"><mat-icon>percent</mat-icon></div>
                  <div class="entity-cell__details"><strong class="entity-cell__name">{{ t.name }}</strong></div>
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="rate">
              <th mat-header-cell *matHeaderCellDef>Rate</th>
              <td mat-cell *matCellDef="let t">{{ t.rate }}{{ t.type === 'percentage' ? '%' : '' }}</td>
            </ng-container>

            <ng-container matColumnDef="type">
              <th mat-header-cell *matHeaderCellDef>Type</th>
              <td mat-cell *matCellDef="let t"><span class="pill">{{ t.type }}</span></td>
            </ng-container>

            <ng-container matColumnDef="inclusive">
              <th mat-header-cell *matHeaderCellDef>Inclusive</th>
              <td mat-cell *matCellDef="let t">{{ t.is_inclusive ? 'Yes' : 'No' }}</td>
            </ng-container>

            <ng-container matColumnDef="status">
              <th mat-header-cell *matHeaderCellDef>Status</th>
              <td mat-cell *matCellDef="let t">
                <span class="status-badge" [class.active]="t.is_active" [class.inactive]="!t.is_active">
                  <span class="status-indicator"></span>{{ t.is_active ? 'Active' : 'Inactive' }}
                </span>
              </td>
            </ng-container>

            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef>Actions</th>
              <td mat-cell *matCellDef="let t">
                <button mat-icon-button [matMenuTriggerFor]="menu" matTooltip="More actions" class="action-menu-trigger">
                  <mat-icon>more_vert</mat-icon>
                </button>
                <mat-menu #menu="matMenu" class="action-menu">
                  <button mat-menu-item (click)="onEdit(t)" *appHasPermission="'tax.update'">
                    <mat-icon>edit</mat-icon><span>Edit</span>
                  </button>
                  <button mat-menu-item (click)="onToggle(t)" *appHasPermission="'tax.update'">
                    <mat-icon>{{ t.is_active ? 'toggle_off' : 'toggle_on' }}</mat-icon>
                    <span>{{ t.is_active ? 'Deactivate' : 'Activate' }}</span>
                  </button>
                  <mat-divider></mat-divider>
                  <button mat-menu-item (click)="onDelete(t)" class="danger-action" *appHasPermission="'tax.delete'">
                    <mat-icon>delete</mat-icon><span>Delete</span>
                  </button>
                </mat-menu>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
          </table>

          <mat-paginator
            [length]="total"
            [pageSize]="pageSize"
            [pageIndex]="pageIndex"
            [pageSizeOptions]="pageSizeOptions"
            [showFirstLastButtons]="true"
            aria-label="Tax pagination"
            (page)="handlePageEvent($event)"
          ></mat-paginator>
        </div>
      </app-card>
    </section>
  `,
  styleUrls: ['../../config-list.shared.scss'],
})
export class TaxesListComponent implements OnInit, OnDestroy {
  items: Tax[] = [];
  loading = false;
  displayedColumns = ['name', 'rate', 'type', 'inclusive', 'status', 'actions'];

  total = 0;
  pageIndex = 0;
  pageSize = 25;
  readonly pageSizeOptions = [10, 25, 50, 100];

  constructor(
    private service: TaxesService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private confirmationService: ConfirmationService,
    private pageHeader: PageHeaderService,
  ) {}

  ngOnInit(): void {
    this.pageHeader.set({
      title: 'Taxes',
      icon: 'percent',
      backRoute: '/dashboard',
      actions: [
        { label: 'Refresh', icon: 'refresh', variant: 'stroked', permission: 'tax.read', disabled: () => this.loading, handler: () => this.load() },
        { label: 'Add Tax', icon: 'add', variant: 'primary', permission: 'tax.create', handler: () => this.onCreate() },
      ],
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.pageHeader.clear();
  }

  load(): void {
    this.loading = true;
    this.service.getPage({ page: this.pageIndex + 1, perPage: this.pageSize }).subscribe({
      next: (page) => {
        this.items = page.items;
        this.total = page.meta.total;
        this.pageSize = page.meta.perPage;
        this.pageIndex = Math.max(page.meta.currentPage - 1, 0);
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Failed to load taxes', 'Close', { duration: 3000 });
        this.loading = false;
      },
    });
  }

  handlePageEvent(event: PageEvent): void {
    if (this.pageSize === event.pageSize && this.pageIndex === event.pageIndex) {
      return;
    }
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.load();
  }

  onCreate(): void {
    this.dialog.open(TaxFormDialogComponent, { width: '560px', maxHeight: '90vh', data: {} })
      .afterClosed().subscribe((saved) => { if (saved) this.load(); });
  }

  onEdit(t: Tax): void {
    this.dialog.open(TaxFormDialogComponent, { width: '560px', maxHeight: '90vh', data: { id: t.id } })
      .afterClosed().subscribe((saved) => { if (saved) this.load(); });
  }

  onToggle(t: Tax): void {
    this.service.toggleActive(t.id).subscribe({
      next: (res) => {
        if (res.success) {
          t.is_active = !t.is_active;
          this.snackBar.open(`Tax ${t.is_active ? 'activated' : 'deactivated'}`, 'Close', { duration: 3000 });
        }
      },
      error: () => this.snackBar.open('Failed to update status', 'Close', { duration: 3000 }),
    });
  }

  onDelete(t: Tax): void {
    this.confirmationService.confirm({
      title: 'Delete tax',
      message: `Are you sure you want to delete "${t.name}"?`,
      confirmText: 'Delete',
      type: 'danger',
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.service.delete(t.id).subscribe({
        next: (res) => {
          if (res.success) {
            this.snackBar.open('Tax deleted', 'Close', { duration: 3000 });
            this.load();
          }
        },
        error: (error) => this.snackBar.open(error.error?.message || 'Failed to delete tax', 'Close', { duration: 5000 }),
      });
    });
  }
}
